/**
 * The composer (`/new` with the feature `film`; docs/SPEC.md §4): one box,
 * **What is the ad about?**, and three ways in — type, paste a web address
 * (read at once by `POST /v1/site`, its findings shown as chips, each opening
 * a preview; a picture or the sentences can be left out, and the logo chip
 * says which logo the film uses: 2026-10-24 fix(composer) site chips), attach
 * (photos or a logo, a spreadsheet, stills of a reference film). Details folded
 * away with the five optional fields. Beside the box: length, aspect, brand kit
 * (None means none) and template. **Start** creates the ad (`POST /v1/ads`) and
 * opens its page; nothing is charged by it.
 */
import { type ReactNode, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  AD_ASPECTS,
  AD_LENGTHS,
  type AdDetail,
  type AdDetails,
  type AdOptions,
  type CreateAdBody,
  DEFAULT_AD_OPTIONS,
  DETAIL_KEYS,
  type DetailKey,
  type SiteExcluded,
  type SiteFindings,
  useCreateAd,
  useReadSite,
} from "../api/ads";
import { useAsset } from "../api/assetlib";
import { useBrandKits } from "../api/brandKits";
import { ApiError, priceAsk, shortfallOf } from "../api/client";
import { CREDITS } from "../copy/credits";
import { PriceConfirm, TopUp } from "../ui/Credits";
import { useMe } from "../api/me";
import { useTemplates } from "../api/templates";
import { type AssetKind, UPLOAD_MIMES } from "../api/types";
import { SHEET_MIMES, uploadImage, uploadSheet } from "../api/uploads";
import {
  ASPECT_NAMES,
  COMPOSER,
  DETAIL_LABELS,
  DETAIL_PLACEHOLDERS,
  say,
} from "../copy/brief";
import { sentenceFor } from "../copy/errors";
import { ImageChip, type ImageStatus, Palette, hostnameOf } from "../ui/Chips";
import { useDebouncedValue } from "../ui/useDebouncedValue";
import { useIdempotencyKey } from "../ui/useIdempotencyKey";
import { errorCodeOf, uploadErrorCopy } from "../library/UploadButton";
import { TemplatePicker } from "./TemplatePicker";
import { Button, Card, Field, Input, PageHeader, Select } from "../ui";

/** How long the box must hold still before a web address in it is read. */
export const SITE_DEBOUNCE_MS = 500;
export const MAX_ATTACHMENTS = 20;
export const MAX_REFERENCE = 8;
export const TEXT_MAX = 4000;
/** How many Objects an ad may be started with (`MAX_CAST` of the API). */
export const MAX_CAST = 4;

/** An object chosen for the cast: its kind in Objects and its id. */
export interface CastPin {
  kind: AssetKind;
  id: string;
}

/** `?pin=<kind>:<id>` (repeatable; "Use in a new ad" from Objects or the
 *  design studio, §23): the objects the film is to be made around. */
export function castFromSearch(search: URLSearchParams): CastPin[] {
  const out: CastPin[] = [];
  for (const value of search.getAll("pin")) {
    const [kind, id] = value.split(":", 2);
    if ((kind !== "shape" && kind !== "variant") || !id) continue;
    if (!/^[a-z0-9][a-z0-9_-]*$/.test(id)) continue;
    if (out.some((p) => p.id === id)) continue;
    out.push({ kind, id });
  }
  return out.slice(0, MAX_CAST);
}

// A host with a dot and a word of two letters or more after it, with or
// without a scheme; never the host of an e-mail address.
const ADDRESS =
  /(?<![@\w.-])((?:https?:\/\/)?(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(?::\d+)?(?:\/[^\s]*)?)/i;

/** The first web address in the box, with `https://` when none was typed;
 *  null when there is none. Trailing punctuation is not part of it. */
export function findAddress(text: string): string | null {
  const found = ADDRESS.exec(text);
  if (!found) return null;
  const raw = found[1].replace(/[.,;:!?)\]"'»]+$/, "");
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
}

// --------------------------------------------------------------- attachments

export type AttachmentRole = "photo" | "sheet" | "reference";

interface Attachment {
  key: string;
  role: AttachmentRole;
  name: string;
  previewUrl: string | null;
  status: ImageStatus;
  uploadId: string | null;
  errorCode: string | null;
  /** A photo that is the person's own logo: the film's logo (`POST /v1/ads`
   *  `logo`). */
  logo: boolean;
}

/** A file whose name says it is a logo (`acme_logo.png`). */
export const LOGO_NAME = /logo/i;

function useAttachments() {
  const [items, setItems] = useState<Attachment[]>([]);
  const seq = useRef(0);
  const urls = useRef(new Set<string>());

  const patch = (key: string, over: Partial<Attachment>) =>
    setItems((list) =>
      list.map((a) => (a.key === key ? { ...a, ...over } : a)),
    );

  const add = (files: File[], role: AttachmentRole) => {
    const room = Math.max(0, MAX_ATTACHMENTS - items.length);
    const accepted = files.slice(0, room);
    // the first photo named a logo is the logo, unless there is one already
    let logoTaken = items.some((a) => a.logo);
    const fresh: Attachment[] = accepted.map((file) => {
      const logo = role === "photo" && !logoTaken && LOGO_NAME.test(file.name);
      logoTaken ||= logo;
      const previewUrl = role === "sheet" ? null : URL.createObjectURL(file);
      if (previewUrl) urls.current.add(previewUrl);
      return {
        key: `att-${++seq.current}`,
        role,
        name: file.name,
        previewUrl,
        status: "uploading",
        uploadId: null,
        errorCode: null,
        logo,
      };
    });
    setItems((list) => [...list, ...fresh]);
    fresh.forEach((chip, i) => {
      const upload = role === "sheet" ? uploadSheet : uploadImage;
      upload(accepted[i]).then(
        (uploadId) => patch(chip.key, { status: "ready", uploadId }),
        (err: unknown) =>
          patch(chip.key, { status: "error", errorCode: errorCodeOf(err) }),
      );
    });
  };

  const remove = (key: string) =>
    setItems((list) => {
      const gone = list.find((a) => a.key === key);
      if (gone?.previewUrl) {
        URL.revokeObjectURL(gone.previewUrl);
        urls.current.delete(gone.previewUrl);
      }
      return list.filter((a) => a.key !== key);
    });

  /** The photo becomes the logo (there is one logo), or a photo again. */
  const toggleLogo = (key: string) =>
    setItems((list) =>
      list.map((a) =>
        a.key === key ? { ...a, logo: !a.logo } : { ...a, logo: false },
      ),
    );

  useEffect(() => {
    const held = urls.current;
    return () => held.forEach((u) => URL.revokeObjectURL(u));
  }, []);

  return { items, add, remove, toggleLogo };
}

function AttachButton({
  label,
  hint,
  accept,
  testId,
  disabled,
  onFiles,
}: {
  label: string;
  hint: string;
  accept: string;
  testId: string;
  disabled: boolean;
  onFiles: (files: File[]) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={input}
        type="file"
        multiple
        accept={accept}
        className="hidden"
        aria-label={label}
        data-testid={testId}
        onChange={(e) => {
          onFiles(Array.from(e.target.files ?? []));
          e.target.value = "";
        }}
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        disabled={disabled}
        title={hint}
        className="min-h-[44px] md:min-h-0"
        onClick={() => input.current?.click()}
      >
        + {label}
      </Button>
    </>
  );
}

// ------------------------------------------------------------- site chips

/** The person's own logo, attached and marked as the logo, as the logo chip
 *  shows it. */
export interface OwnLogo {
  name: string;
  previewUrl: string | null;
}

type FindingKey = "logo" | "colours" | "pictures" | "tagline" | "fonts";

export const NOTHING_LEFT_OUT: SiteExcluded = {
  pictures: [],
  sentences: false,
};

/** One picture of a logo in the logo chip's preview, said used or not. */
function LogoFigure({
  src,
  caption,
  used,
}: {
  src: string | null;
  caption: string;
  used: boolean;
}) {
  return (
    <figure
      className={[
        "m-0 flex w-40 flex-col items-center gap-1.5 rounded-md border bg-white p-2",
        used ? "border-brand" : "border-slate-200 opacity-70",
      ].join(" ")}
      data-testid="logo-figure"
      data-used={used}
    >
      {src ? (
        <img src={src} alt={caption} className="h-16 w-full object-contain" />
      ) : (
        <span className="h-16" />
      )}
      <figcaption className="w-full min-w-0 text-center text-xs text-slate-600">
        <span className="block break-words [overflow-wrap:anywhere]">
          {caption}
        </span>
        <span
          className={[
            "mt-1 block font-semibold",
            used ? "text-brand-dark" : "text-slate-500",
          ].join(" ")}
        >
          {used ? COMPOSER.siteLogoUsed : COMPOSER.siteLogoNotUsed}
        </span>
      </figcaption>
    </figure>
  );
}

function SiteChips({
  url,
  findings,
  reading,
  failed,
  excluded,
  ownLogo,
  kitLogoName,
  onExclude,
  onRemove,
}: {
  url: string;
  findings: SiteFindings | null;
  reading: boolean;
  failed: boolean;
  /** What the person left out of the findings (sent with Start). */
  excluded: SiteExcluded;
  /** The attached photo marked as the logo: the film uses it (`ad_logo`). */
  ownLogo: OwnLogo | null;
  /** The chosen brand kit's name when it has a logo: used before the site's. */
  kitLogoName: string | null;
  onExclude: (next: SiteExcluded) => void;
  onRemove: () => void;
}) {
  const host = hostnameOf(url);
  const [open, setOpen] = useState<FindingKey | null>(null);
  const chips: { key: FindingKey; what: string; node: ReactNode }[] = [];
  // a picture keeps its place in the site's answer (1 is the first): the place
  // is what Start sends for one left out
  const pictures = (findings?.pictures ?? []).map((p, i) => ({
    ...p,
    n: i + 1,
  }));
  const kept = pictures.filter((p) => !excluded.pictures.includes(p.n));
  if (findings) {
    if (findings.logo) {
      const label = ownLogo
        ? COMPOSER.siteLogoYours
        : kitLogoName
          ? say(COMPOSER.siteLogoKit, { name: kitLogoName })
          : COMPOSER.siteLogo;
      chips.push({
        key: "logo",
        what: label,
        node: (
          <>
            <img
              src={ownLogo?.previewUrl ?? findings.logo.url}
              alt=""
              className="h-4 w-4 rounded-sm object-contain"
            />
            {label}
          </>
        ),
      });
    }
    const c = findings.colours.length;
    if (c > 0) {
      const label =
        c === 1 ? COMPOSER.siteColour : say(COMPOSER.siteColours, { n: c });
      chips.push({
        key: "colours",
        what: label,
        node: (
          <>
            <Palette colours={findings.colours} />
            {label}
          </>
        ),
      });
    }
    if (pictures.length > 0) {
      const p = kept.length;
      const label =
        p === 0
          ? COMPOSER.sitePicturesNone
          : p === 1
            ? COMPOSER.sitePicture
            : say(COMPOSER.sitePictures, { n: p });
      chips.push({ key: "pictures", what: label, node: label });
    }
    if (findings.sentences.length > 0) {
      const label = excluded.sentences
        ? COMPOSER.siteTaglineOut
        : COMPOSER.siteTagline;
      chips.push({ key: "tagline", what: label, node: label });
    }
    if (findings.fonts.length > 0) {
      const label = findings.fonts.slice(0, 2).join(", ");
      chips.push({ key: "fonts", what: label, node: label });
    }
  }
  const shown = chips.some((c) => c.key === open) ? open : null;
  const panelId = `site-found-${host.replace(/[^a-z0-9]/gi, "-")}`;

  let panel: ReactNode = null;
  if (findings && shown === "logo" && findings.logo) {
    panel = (
      <>
        <div className="flex flex-wrap gap-3">
          {ownLogo ? (
            <LogoFigure
              src={ownLogo.previewUrl}
              caption={`${COMPOSER.siteLogoMine} · ${ownLogo.name}`}
              used
            />
          ) : null}
          <LogoFigure
            src={findings.logo.url}
            caption={say(COMPOSER.siteLogoOnSite, { host })}
            used={!ownLogo && !kitLogoName}
          />
        </div>
        {ownLogo ? (
          <p className="m-0 text-xs text-slate-600">
            {COMPOSER.siteLogoYoursUsed}
          </p>
        ) : kitLogoName ? (
          <p className="m-0 text-xs text-slate-600">
            {say(COMPOSER.siteLogoKitUsed, { name: kitLogoName })}
          </p>
        ) : null}
      </>
    );
  } else if (findings && shown === "pictures") {
    const out = pictures.length - kept.length;
    panel = (
      <>
        <p className="m-0 text-xs text-slate-600">
          {say(COMPOSER.sitePicturesHint, { host })}
        </p>
        {kept.length > 0 ? (
          <ul
            className="m-0 grid list-none grid-cols-3 gap-2 p-0 sm:grid-cols-4 md:grid-cols-6"
            data-testid="site-pictures"
          >
            {kept.map((p) => (
              <li
                key={p.n}
                className="relative aspect-square overflow-hidden rounded-md border border-slate-200 bg-slate-100"
              >
                <img
                  src={p.url}
                  alt={say(COMPOSER.sitePictureAlt, { n: p.n, host })}
                  className="h-full w-full object-cover"
                />
                <button
                  type="button"
                  aria-label={say(COMPOSER.sitePictureRemove, { n: p.n })}
                  title={say(COMPOSER.sitePictureRemove, { n: p.n })}
                  onClick={() =>
                    onExclude({
                      ...excluded,
                      pictures: [...excluded.pictures, p.n].sort(
                        (a, b) => a - b,
                      ),
                    })
                  }
                  className="absolute right-1 top-1 inline-flex h-7 w-7 items-center justify-center rounded-pill bg-white/90 text-base text-slate-700 shadow hover:bg-white hover:text-slate-950"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {out > 0 ? (
          <button
            type="button"
            className="self-start text-xs font-semibold text-brand-dark underline"
            onClick={() => onExclude({ ...excluded, pictures: [] })}
          >
            {say(COMPOSER.sitePicturesBack, { n: out })}
          </button>
        ) : null}
      </>
    );
  } else if (findings && shown === "tagline") {
    panel = (
      <>
        <p className="m-0 text-xs text-slate-600">
          {say(COMPOSER.siteTaglineHint, { host })}
        </p>
        <blockquote
          className={[
            "m-0 border-l-2 border-slate-300 pl-3 text-sm",
            excluded.sentences
              ? "text-slate-400 line-through"
              : "text-slate-800",
          ].join(" ")}
          data-testid="site-sentences"
        >
          {findings.sentences.map((line, i) => (
            <p key={i} className="m-0">
              {line}
            </p>
          ))}
        </blockquote>
        {excluded.sentences ? (
          <p className="m-0 text-xs text-slate-600">
            {COMPOSER.siteTaglineGone}
          </p>
        ) : null}
        <button
          type="button"
          className="self-start text-xs font-semibold text-brand-dark underline"
          onClick={() =>
            onExclude({ ...excluded, sentences: !excluded.sentences })
          }
        >
          {excluded.sentences
            ? COMPOSER.siteTaglineBack
            : COMPOSER.siteTaglineRemove}
        </button>
      </>
    );
  } else if (findings && shown === "colours") {
    panel = (
      <>
        <p className="m-0 text-xs text-slate-600">
          {say(COMPOSER.siteColoursHint, { host })}
        </p>
        <ul
          className="m-0 flex list-none flex-wrap gap-3 p-0"
          data-testid="site-colours"
        >
          {findings.colours.map((colour, i) => (
            <li key={`${colour}-${i}`} className="flex items-center gap-2">
              <i
                className="inline-block h-8 w-8 rounded-md border border-black/10"
                style={{ background: colour }}
              />
              <code className="text-xs text-slate-700">{colour}</code>
            </li>
          ))}
        </ul>
      </>
    );
  } else if (findings && shown === "fonts") {
    panel = (
      <>
        <p className="m-0 text-xs text-slate-600">
          {say(COMPOSER.siteFontsHint, { host })}
        </p>
        <ul
          className="m-0 flex list-none flex-col gap-1 p-0"
          data-testid="site-fonts"
        >
          {findings.fonts.map((font) => (
            <li key={font} className="text-sm text-slate-800">
              {font}
            </li>
          ))}
        </ul>
      </>
    );
  }

  return (
    <div className="flex flex-col gap-1.5" data-testid="site">
      <ul
        className="m-0 flex list-none flex-wrap items-center gap-2 p-0"
        aria-label={COMPOSER.siteFrom}
      >
        <li
          className="tb-pill tb-pill-sm tb-pill-purple inline-flex items-center gap-1.5"
          data-testid="site-chip"
          title={url}
        >
          <span className="max-w-[16rem] truncate">{host}</span>
          <button
            type="button"
            aria-label={say(COMPOSER.siteRemove, { host })}
            title={say(COMPOSER.siteRemove, { host })}
            onClick={onRemove}
            className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-pill hover:bg-white/60"
          >
            ×
          </button>
        </li>
        {chips.map((chip) => (
          <li key={chip.key} data-testid="finding-chip" data-finding={chip.key}>
            <button
              type="button"
              aria-expanded={shown === chip.key}
              aria-controls={panelId}
              title={say(COMPOSER.siteShow, { what: chip.what })}
              onClick={() => setOpen(shown === chip.key ? null : chip.key)}
              className={[
                "tb-pill tb-pill-sm inline-flex cursor-pointer items-center gap-1.5",
                shown === chip.key ? "ring-2 ring-brand-light" : "",
                (chip.key === "tagline" && excluded.sentences) ||
                (chip.key === "pictures" && kept.length === 0)
                  ? "text-slate-400 line-through"
                  : "",
              ].join(" ")}
            >
              {chip.node}
            </button>
          </li>
        ))}
      </ul>
      {panel ? (
        <section
          id={panelId}
          aria-label={chips.find((c) => c.key === shown)?.what}
          className="relative flex flex-col gap-2 rounded-md border border-slate-200 bg-white/80 p-3 pr-10"
          data-testid="site-preview"
          data-finding={shown}
        >
          <button
            type="button"
            aria-label={COMPOSER.siteClose}
            title={COMPOSER.siteClose}
            onClick={() => setOpen(null)}
            className="absolute right-1.5 top-1.5 inline-flex h-7 w-7 items-center justify-center rounded-pill text-slate-500 hover:bg-slate-100 hover:text-slate-900"
          >
            ×
          </button>
          {panel}
        </section>
      ) : null}
      {reading ? (
        <p role="status" className="m-0 text-xs text-slate-500">
          {say(COMPOSER.siteReading, { host })}
        </p>
      ) : failed ? (
        <p role="status" className="m-0 text-xs text-slate-600">
          {say(COMPOSER.siteFailed, { host })}
        </p>
      ) : null}
    </div>
  );
}

// --------------------------------------------------------- option controls

function Segmented<T extends string | number>({
  label,
  value,
  values,
  format = String,
  onChange,
}: {
  label: string;
  value: T;
  values: readonly T[];
  format?: (v: T) => string;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="tb-field-label">{label}</span>
      <div
        role="radiogroup"
        aria-label={label}
        className="grid gap-1.5"
        style={{
          gridTemplateColumns: `repeat(${values.length}, minmax(0, 1fr))`,
        }}
      >
        {values.map((v) => {
          const checked = v === value;
          return (
            <button
              key={String(v)}
              type="button"
              role="radio"
              aria-checked={checked}
              onClick={() => onChange(v)}
              className={[
                "min-h-[40px] whitespace-nowrap rounded-pill border px-1 py-1.5 text-center text-sm transition-colors md:min-h-0",
                checked
                  ? "border-brand bg-brand-tint/60 font-semibold text-brand-dark"
                  : "border-slate-200 bg-white text-slate-700 hover:border-brand-light",
              ].join(" ")}
            >
              {format(v)}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------- cast

/** An object chosen for the cast, by its name in Objects (its id until the
 *  detail arrives). */
function CastChip({ pin, onRemove }: { pin: CastPin; onRemove: () => void }) {
  const detail = useAsset(pin.kind, pin.id);
  const name =
    (detail.data?.meta?.name as string | undefined) ??
    pin.id.replace(/-/g, " ");
  return (
    <ImageChip
      name={name}
      previewUrl={detail.data?.files?.["thumb.jpg"] ?? null}
      status="ready"
      error={null}
      onRemove={onRemove}
    />
  );
}

// ------------------------------------------------------------------- page

export function Composer() {
  const navigate = useNavigate();
  const me = useMe();
  const create = useCreateAd();
  const readSite = useReadSite();
  const brandKits = useBrandKits();
  const templates = useTemplates();
  const attachments = useAttachments();

  const [text, setText] = useState("");
  const [details, setDetails] = useState<AdDetails>({});
  const [options, setOptions] = useState<AdOptions>(DEFAULT_AD_OPTIONS);
  // `undefined` until the select is touched: the organisation's default kit,
  // which the API applies when the field is absent. "None" is sent as null.
  const [kitChoice, setKitChoice] = useState<string | null | undefined>(
    undefined,
  );
  // `?template=<id>`: opened from a template card (P22-T05), that template
  // is chosen.
  const [search] = useSearchParams();
  const [templateId, setTemplateId] = useState<string | null>(
    () => search.get("template") || null,
  );
  const [cast, setCast] = useState<CastPin[]>(() => castFromSearch(search));

  // The web address in the box, read once it holds still.
  const [left, setLeft] = useState<string | null>(null);
  const [sites, setSites] = useState<
    Record<string, SiteFindings | "failed" | "reading">
  >({});
  const typed = findAddress(text);
  const address = typed && typed !== left ? typed : null;
  const settled = useDebouncedValue(address, SITE_DEBOUNCE_MS);
  const readRef = useRef(readSite.mutate);
  readRef.current = readSite.mutate;
  const asked = useRef(new Set<string>());
  useEffect(() => {
    if (!settled || asked.current.has(settled)) return;
    asked.current.add(settled);
    setSites((s) => ({ ...s, [settled]: "reading" }));
    readRef.current(settled, {
      onSuccess: (found) => setSites((s) => ({ ...s, [settled]: found })),
      onError: () => setSites((s) => ({ ...s, [settled]: "failed" })),
    });
  }, [settled]);
  const site = address ? sites[address] : undefined;
  // what the person left out of each site's findings
  const [leftOut, setLeftOut] = useState<Record<string, SiteExcluded>>({});
  const excluded = (address && leftOut[address]) || NOTHING_LEFT_OUT;

  const kits = brandKits.data?.items ?? [];
  const defaultKit = kits.find((k) => k.is_default) ?? null;
  const shownKit =
    kitChoice === undefined ? (defaultKit?.id ?? null) : kitChoice;
  const chosenKit = kits.find((k) => k.id === shownKit) ?? null;

  const ready = attachments.items.filter(
    (a) => a.status === "ready" && a.uploadId,
  );
  const uploading = attachments.items.some((a) => a.status === "uploading");
  const logoAttachment =
    attachments.items.find(
      (a) => a.role === "photo" && a.logo && a.status !== "error",
    ) ?? null;
  const filledDetails = Object.fromEntries(
    Object.entries(details)
      .map(([k, v]) => [k, (v ?? "").trim()])
      .filter(([, v]) => v),
  ) as AdDetails;
  const empty =
    !text.trim() &&
    !address &&
    ready.length === 0 &&
    cast.length === 0 &&
    Object.keys(filledDetails).length === 0;

  const body: CreateAdBody = {
    text: text.trim(),
    details: filledDetails,
    ...(address ? { url: address } : {}),
    uploads: ready
      .filter((a) => a.role !== "reference")
      .map((a) => a.uploadId!),
    reference: ready
      .filter((a) => a.role === "reference")
      .map((a) => a.uploadId!),
    options,
    ...(kitChoice !== undefined ? { brand_kit_id: kitChoice } : {}),
    ...(templateId ? { template_id: templateId } : {}),
    ...(cast.length > 0 ? { cast: cast.map((p) => `objects:${p.id}`) } : {}),
    ...(logoAttachment?.uploadId ? { logo: logoAttachment.uploadId } : {}),
    ...(address && (excluded.pictures.length > 0 || excluded.sentences)
      ? { site_excluded: excluded }
      : {}),
  };
  const idempotencyKey = useIdempotencyKey(JSON.stringify(body));
  // the yes is another write: its own key (the first answer, the question, is
  // what the API keeps for the first)
  const confirmKey = useIdempotencyKey(`${JSON.stringify(body)}:yes`);
  const canStart = !empty && !uploading && !create.isPending;
  // §15.1: the brief's first turn waits for a yes at or above
  // `PRICE_CONFIRM_CREDITS`; the estimate is the body's
  const [priced, setPriced] = useState<{
    body: string;
    credits: number;
  } | null>(null);
  const shownPrice = priced?.body === JSON.stringify(body) ? priced : null;

  const start = (confirm = false) => {
    if (!canStart) return;
    setPriced(null);
    create.mutate(
      confirm
        ? { ...body, confirm: true, idempotencyKey: confirmKey }
        : { ...body, idempotencyKey },
      {
        onSuccess: (made) => {
          const ask = priceAsk(made);
          if (ask) {
            setPriced({ body: JSON.stringify(body), credits: ask.estimate });
          } else {
            navigate(`/ads/${encodeURIComponent((made as AdDetail).id)}`);
          }
        },
      },
    );
  };

  const createError = create.error;
  const createShort = shortfallOf(createError);
  // A spreadsheet the API could not read, or §4.2's empty box, comes back as
  // 400 `invalid_options` with its own sentence (backend copy tables).
  const createSentence =
    createError instanceof ApiError &&
    createError.code === "invalid_options" &&
    createError.message
      ? createError.message
      : createError
        ? sentenceFor(createError)
        : null;

  const references = attachments.items.filter((a) => a.role === "reference");
  const imports = me.data?.features?.imports === true;

  return (
    <>
      <PageHeader eyebrow={COMPOSER.eyebrow} title={COMPOSER.title} />
      <form
        className="grid items-start gap-6 md:grid-cols-[minmax(0,1fr)_300px]"
        onSubmit={(e) => {
          e.preventDefault();
          start();
        }}
      >
        <Card className="flex min-w-0 flex-col gap-4 p-5 md:col-start-1 md:row-start-1 md:p-6">
          {/* The page's heading is the box's question (§4.1): the box is
                named by it, not labelled twice. */}
          <Field helper={COMPOSER.boxHelper}>
            <textarea
              aria-label={COMPOSER.boxLabel}
              className="tb-input min-h-[180px] resize-y text-base"
              value={text}
              maxLength={TEXT_MAX}
              placeholder={COMPOSER.boxPlaceholder}
              onChange={(e) => setText(e.target.value)}
            />
          </Field>
          {cast.length > 0 ? (
            <div className="flex flex-col gap-1" data-testid="cast">
              <span className="tb-field-label">{COMPOSER.cast}</span>
              <ul
                className="m-0 flex list-none flex-wrap gap-2 p-0"
                aria-label={COMPOSER.cast}
              >
                {cast.map((pin) => (
                  <CastChip
                    key={pin.id}
                    pin={pin}
                    onRemove={() =>
                      setCast((c) => c.filter((p) => p.id !== pin.id))
                    }
                  />
                ))}
              </ul>
              <p className="m-0 text-xs text-slate-500">{COMPOSER.castHint}</p>
            </div>
          ) : null}
          {address ? (
            <SiteChips
              url={address}
              findings={
                site && site !== "failed" && site !== "reading" ? site : null
              }
              reading={site === "reading" || site === undefined}
              failed={site === "failed"}
              excluded={excluded}
              ownLogo={
                logoAttachment
                  ? {
                      name: logoAttachment.name,
                      previewUrl: logoAttachment.previewUrl,
                    }
                  : null
              }
              kitLogoName={chosenKit?.logo_asset_id ? chosenKit.name : null}
              onExclude={(next) =>
                setLeftOut((all) => ({ ...all, [address]: next }))
              }
              onRemove={() => setLeft(address)}
            />
          ) : null}
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-1">
              <span className="tb-field-label mr-1">{COMPOSER.attach}</span>
              <AttachButton
                label={COMPOSER.attachPhotos}
                hint={COMPOSER.attachPhotosHint}
                accept={UPLOAD_MIMES.join(",")}
                testId="photo-input"
                disabled={attachments.items.length >= MAX_ATTACHMENTS}
                onFiles={(files) => attachments.add(files, "photo")}
              />
              <AttachButton
                label={COMPOSER.attachSheet}
                hint={COMPOSER.attachSheetHint}
                accept={[...SHEET_MIMES, ".csv", ".xlsx"].join(",")}
                testId="sheet-input"
                disabled={attachments.items.length >= MAX_ATTACHMENTS}
                onFiles={(files) => attachments.add(files, "sheet")}
              />
              <AttachButton
                label={COMPOSER.attachReference}
                hint={COMPOSER.attachReferenceHint}
                accept={UPLOAD_MIMES.join(",")}
                testId="reference-input"
                disabled={
                  attachments.items.length >= MAX_ATTACHMENTS ||
                  references.length >= MAX_REFERENCE
                }
                onFiles={(files) =>
                  attachments.add(
                    files.slice(0, MAX_REFERENCE - references.length),
                    "reference",
                  )
                }
              />
            </div>
            {attachments.items.length > 0 ? (
              <ul
                className="m-0 flex list-none flex-wrap gap-2 p-0"
                aria-label={COMPOSER.attach}
              >
                {attachments.items.map((a) => (
                  <ImageChip
                    key={a.key}
                    name={
                      a.role === "sheet"
                        ? `${a.name} · ${COMPOSER.sheetRole}`
                        : a.role === "reference"
                          ? `${a.name} · ${COMPOSER.referenceRole}`
                          : a.name
                    }
                    previewUrl={a.previewUrl}
                    status={a.status}
                    error={
                      a.errorCode
                        ? a.role === "sheet" &&
                          a.errorCode === "asset_not_image"
                          ? COMPOSER.notSheet
                          : uploadErrorCopy(a.errorCode)
                        : null
                    }
                    extra={
                      a.role === "photo" && a.status !== "error" ? (
                        <button
                          type="button"
                          aria-pressed={a.logo}
                          aria-label={say(
                            a.logo
                              ? COMPOSER.attachLogoIs
                              : COMPOSER.attachLogoMake,
                            { name: a.name },
                          )}
                          title={say(
                            a.logo
                              ? COMPOSER.attachLogoIs
                              : COMPOSER.attachLogoMake,
                            { name: a.name },
                          )}
                          onClick={() => attachments.toggleLogo(a.key)}
                          className={[
                            "rounded-pill border px-1.5 text-[11px] leading-4",
                            a.logo
                              ? "border-brand bg-brand-tint/60 font-semibold text-brand-dark"
                              : "border-slate-300 text-slate-500 hover:border-brand-light",
                          ].join(" ")}
                        >
                          {COMPOSER.attachLogo}
                        </button>
                      ) : null
                    }
                    onRemove={() => attachments.remove(a.key)}
                  />
                ))}
              </ul>
            ) : null}
            {references.length > 0 ? (
              <p className="m-0 text-xs text-slate-500">
                {COMPOSER.attachReferenceHint}
              </p>
            ) : null}
            {imports ? (
              <p className="m-0 text-xs text-slate-500">
                {COMPOSER.attachModel}{" "}
                <Link to="/assets/import">{COMPOSER.attachModelLink}</Link>
              </p>
            ) : null}
          </div>
          <details className="group rounded-md border border-slate-200 bg-white/60 px-4 py-2">
            <summary className="cursor-pointer py-1 text-sm font-semibold text-slate-700">
              {COMPOSER.details}
              <span className="ml-2 font-normal text-slate-500">
                {COMPOSER.detailsHint}
              </span>
            </summary>
            <div className="grid gap-3 py-3 md:grid-cols-2">
              {DETAIL_KEYS.map((key: DetailKey) => (
                <Field key={key} label={DETAIL_LABELS[key]}>
                  <Input
                    value={details[key] ?? ""}
                    placeholder={DETAIL_PLACEHOLDERS[key]}
                    maxLength={500}
                    onChange={(e) =>
                      setDetails((d) => ({ ...d, [key]: e.target.value }))
                    }
                  />
                </Field>
              ))}
            </div>
          </details>
        </Card>

        {/* On a phone the options and Start come right after the box; the
            templates follow. */}
        <Card className="flex h-fit flex-col gap-5 p-5 md:sticky md:top-24 md:col-start-2 md:row-span-2 md:row-start-1 md:p-6">
          <Segmented
            label={COMPOSER.length}
            value={options.length_s}
            values={AD_LENGTHS}
            format={(v) => `${v} s`}
            onChange={(v) => setOptions((o) => ({ ...o, length_s: v }))}
          />
          <Segmented
            label={COMPOSER.aspect}
            value={options.aspect}
            values={AD_ASPECTS}
            format={(v) => ASPECT_NAMES[v] ?? v}
            onChange={(v) => setOptions((o) => ({ ...o, aspect: v }))}
          />
          {kits.length > 0 ? (
            <Field
              label={COMPOSER.brandKit}
              helper={
                chosenKit
                  ? say(COMPOSER.brandKitHelperKit, { name: chosenKit.name })
                  : COMPOSER.brandKitHelperNone
              }
            >
              <Select
                value={shownKit ?? ""}
                onChange={(e) => setKitChoice(e.target.value || null)}
              >
                <option value="">{COMPOSER.brandKitNone}</option>
                {kits.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.is_default
                      ? say(COMPOSER.brandKitDefault, { name: k.name })
                      : k.name}
                  </option>
                ))}
              </Select>
            </Field>
          ) : brandKits.data ? (
            <p className="m-0 text-sm text-slate-500">
              {COMPOSER.noKits} <Link to="/brand">{COMPOSER.noKitsLink}</Link>
            </p>
          ) : null}
          <div className="flex flex-col gap-2 border-t border-slate-200 pt-4">
            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={!canStart}
              aria-busy={create.isPending}
              aria-describedby="start-why"
            >
              {create.isPending ? COMPOSER.starting : COMPOSER.start}
            </Button>
            <p id="start-why" className="m-0 text-xs text-slate-500">
              {empty
                ? COMPOSER.empty
                : uploading
                  ? COMPOSER.uploading
                  : CREDITS.composer}
            </p>
            {shownPrice ? (
              <PriceConfirm
                what="start"
                credits={shownPrice.credits}
                busy={create.isPending}
                onYes={() => start(true)}
                onNo={() => setPriced(null)}
              />
            ) : null}
            {createShort ? (
              <TopUp shortfall={createShort} />
            ) : createSentence ? (
              <p role="alert" className="m-0 text-sm text-slate-700">
                {createSentence}
              </p>
            ) : null}
          </div>
        </Card>
        {templates.data && templates.data.length > 0 ? (
          <Card className="min-w-0 p-5 md:col-start-1 md:row-start-2 md:p-6">
            <TemplatePicker
              templates={templates.data}
              selected={templateId}
              onSelect={setTemplateId}
            />
          </Card>
        ) : null}
      </form>
    </>
  );
}
